import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { BasePDFDocument } from '@/shared/components/pdf/BasePDFDocument';
import { Shot } from '@/features/shotlist';

interface ShotListPDFTemplateProps {
    projectTitle: string;
    scenesData: { sceneName: string; shots: Shot[] }[];
    brandLogoBase64?: string;
}

const styles = StyleSheet.create({
    // Shotlist specific
    headerContent: {
        alignItems: 'center',
        marginBottom: 30,
        paddingBottom: 20,
        borderBottomWidth: 2,
        borderBottomColor: '#e2e8f0',
        borderBottomStyle: 'solid'
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        color: '#1a202c',
        marginBottom: 8,
        textTransform: 'uppercase'
    },
    metaHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    metaText: {
        fontSize: 10,
        color: '#718096',
        textTransform: 'uppercase',
        letterSpacing: 1
    },
    metaDot: {
        fontSize: 10,
        color: '#cbd5e0'
    },
    
    sceneContainer: {
        marginBottom: 30,
    },
    sceneHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#667eea',
        padding: 10,
        borderRadius: 4,
        marginBottom: 10
    },
    sceneTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#ffffff',
    },
    shotCountBadge: {
        fontSize: 9,
        color: '#ffffff',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 8,
        fontWeight: 'bold'
    },
    
    // Table 
    table: {
        width: '100%',
        borderWidth: 1,
        borderColor: '#e2e8f0',
        borderStyle: 'solid',
        borderRadius: 4,
    },
    tableHeaderRow: {
        flexDirection: 'row',
        backgroundColor: '#f8f9fa',
        borderBottomWidth: 1,
        borderBottomColor: '#cbd5e1',
        borderBottomStyle: 'solid',
        padding: 8,
    },
    tableHeaderCell: {
        fontSize: 9,
        fontWeight: 'bold',
        color: '#475569',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e2e8f0',
        borderBottomStyle: 'solid',
        padding: 8,
        alignItems: 'flex-start'
    },
    tableRowEven: {
        backgroundColor: '#f8fafc'
    },
    
    // Columns (Total 100%)
    colIdx: { width: '5%' },
    colDetails: { width: '25%', paddingRight: 6 },
    colSubject: { width: '15%', paddingRight: 6 },
    colDesc: { width: '30%', paddingRight: 6 },
    colPurpose: { width: '15%', paddingRight: 6 },
    colTime: { width: '10%', textAlign: 'center' },
    
    // Cell styling
    cellText: { fontSize: 9, color: '#334155', lineHeight: 1.3 },
    cellTextBold: { fontSize: 9, fontWeight: 'bold', color: '#0f172a', marginBottom: 2 },
    cellTextMuted: { fontSize: 8, color: '#64748b' },
    cellIdxText: { fontSize: 9, fontWeight: 'bold', color: '#667eea' },
    cellTimeText: { fontSize: 9, fontWeight: 'bold', color: '#667eea', textAlign: 'center' },
});

export const ShotListPDFTemplate: React.FC<ShotListPDFTemplateProps> = ({
    projectTitle,
    scenesData,
    brandLogoBase64
}) => {
    const date = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    return (
        <BasePDFDocument brandLogoBase64={brandLogoBase64}>
            {/* Main Content Header */}
            <View style={styles.headerContent} fixed>
                <Text style={styles.title}>{projectTitle}</Text>
                <View style={styles.metaHeader}>
                    <Text style={styles.metaText}>Shot List</Text>
                    <Text style={styles.metaDot}>•</Text>
                    <Text style={styles.metaText}>{date}</Text>
                </View>
            </View>

            {scenesData.length === 0 ? (
                <Text style={{ textAlign: 'center', marginTop: 40, color: '#9ca3af', fontStyle: 'italic', fontSize: 12 }}>
                    No scenes or shots to display.
                </Text>
            ) : (
                scenesData.map((scene, sceneIndex) => (
                    <View key={sceneIndex} style={styles.sceneContainer} wrap={false}>
                        {/* Scene Header */}
                        <View style={styles.sceneHeader}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                <Text style={[styles.shotCountBadge, { backgroundColor: 'rgba(255,255,255,0.3)' }]}>
                                    Scene {sceneIndex + 1}
                                </Text>
                                <Text style={styles.sceneTitle}>
                                    {scene.sceneName || `Scene ${sceneIndex + 1}`}
                                </Text>
                            </View>
                            <Text style={styles.shotCountBadge}>
                                {scene.shots.length} Shot{scene.shots.length !== 1 ? 's' : ''}
                            </Text>
                        </View>

                        {/* Shots Table */}
                        <View style={styles.table}>
                            {/* Header Row */}
                            <View style={styles.tableHeaderRow}>
                                <Text style={[styles.tableHeaderCell, styles.colIdx]}>#</Text>
                                <Text style={[styles.tableHeaderCell, styles.colDetails]}>Details</Text>
                                <Text style={[styles.tableHeaderCell, styles.colSubject]}>Subject</Text>
                                <Text style={[styles.tableHeaderCell, styles.colDesc]}>Description</Text>
                                <Text style={[styles.tableHeaderCell, styles.colPurpose]}>Purpose</Text>
                                <Text style={[styles.tableHeaderCell, styles.colTime]}>Time</Text>
                            </View>

                            {/* Rows */}
                            {scene.shots.length > 0 ? (
                                scene.shots.map((shot, shotIndex) => {
                                    const isEven = shotIndex % 2 === 0;
                                    return (
                                        <View key={shot.id} style={[styles.tableRow, isEven ? styles.tableRowEven : {}]} wrap={false}>
                                            <View style={styles.colIdx}>
                                                <Text style={styles.cellIdxText}>{shotIndex + 1}</Text>
                                            </View>
                                            <View style={styles.colDetails}>
                                                <Text style={styles.cellTextBold}>{shot.shotType}</Text>
                                                <Text style={styles.cellTextMuted}>
                                                    {[shot.cameraAngle, shot.cameraMovement].filter(Boolean).join(' • ')}
                                                </Text>
                                            </View>
                                            <View style={styles.colSubject}>
                                                <Text style={styles.cellText}>{shot.subject || '—'}</Text>
                                            </View>
                                            <View style={styles.colDesc}>
                                                <Text style={styles.cellText}>{shot.description || '—'}</Text>
                                            </View>
                                            <View style={styles.colPurpose}>
                                                <Text style={styles.cellTextMuted}>{shot.emotionalPurpose || '—'}</Text>
                                            </View>
                                            <View style={styles.colTime}>
                                                <Text style={styles.cellTimeText}>{shot.duration || '—'}</Text>
                                            </View>
                                        </View>
                                    );
                                })
                            ) : (
                                <View style={styles.tableRow}>
                                    <Text style={{ fontSize: 9, color: '#9ca3af', fontStyle: 'italic', padding: 8 }}>
                                        No shots fully detailed in this scene.
                                    </Text>
                                </View>
                            )}
                        </View>
                    </View>
                ))
            )}
        </BasePDFDocument>
    );
};
